import type { BillPaymentExecutionResponse } from 'src/queries/payments/payments.types'

/**
 * The execution shape this mapper reads. Only the fields that drive the copy —
 * the public status plus the enriched failure detail — are required, so callers
 * may pass a full `BillPaymentExecutionResponse` or a narrowed projection.
 */
export type PaymentFailureExecution = Pick<
  BillPaymentExecutionResponse,
  'status' | 'failureReason' | 'returnCode' | 'failedLeg'
>

/**
 * Plain-language, processor-agnostic reason for a terminal Bill Payment that
 * FAILED or was RETURNED, derived from the execution detail.
 *
 * The result is an i18n KEY (key === message in `en-US.json`); the caller passes
 * it through `translate()`. Returns `null` for any non-terminal status
 * (PROCESSING / COMPLETED / …) so callers can skip rendering a reason.
 *
 * Branch order matches the ticket copy matrix: public status (RETURNED vs
 * FAILED) → failed leg → ACH return code. Rules that must hold:
 *  - never names the payment processor (Modern Treasury or otherwise);
 *  - never mentions retry;
 *  - each string is one sentence or two short ones;
 *  - a RETURNED with no `failedLeg`, and a FAILED with no `failureReason`, fall
 *    back to a generic sentence (older rows, or the API projection not yet
 *    deployed).
 */
export const paymentFailureReason = (
  execution: PaymentFailureExecution | null | undefined,
): string | null => {
  if (!execution) return null
  const { status, failureReason, returnCode, failedLeg } = execution

  if (status === 'RETURNED') {
    if (failedLeg === 'PAYOUT') {
      switch (returnCode) {
        case 'R01':
          return "The vendor's bank returned the payment due to insufficient funds in the receiving account."
        case 'R02':
          return "The vendor's bank returned the payment — the account is closed."
        case 'R03':
          return "The vendor's bank returned the payment — the account could not be found. Check the vendor's bank details."
        case 'R04':
          return "The vendor's bank returned the payment — the account number is invalid. Check the vendor's bank details."
        case 'R08':
          return "The vendor's bank returned the payment — the payment was stopped."
        case 'R16':
          return "The vendor's bank returned the payment — the account is frozen."
        case 'R20':
          return "The vendor's bank returned the payment — the account can't accept payments."
        default:
          return "The vendor's bank returned the payment."
      }
    }
    if (failedLeg === 'FUNDING') {
      switch (returnCode) {
        case 'R01':
          return 'The payment was returned because your bank account had insufficient funds.'
        case 'R02':
        case 'R03':
        case 'R04':
          return 'The payment was returned by your bank — check your bank account details in Settings.'
        default:
          return 'The payment was returned by your bank.'
      }
    }
    // RETURNED with no leg detail (older rows / API projection not deployed).
    return 'The payment was returned by the bank.'
  }

  if (status === 'FAILED') {
    if (failureReason === 'PROVIDER_REJECTED') {
      return 'The payment could not be processed by the bank.'
    }
    if (failureReason === 'PRECONDITION_FAILED') {
      return "The payment couldn't be started because of a bank account setup issue. Contact support."
    }
    // FAILED with no reason detail (older rows / API projection not deployed).
    return 'This payment could not be completed.'
  }

  return null
}
